/**
 * `/company-blocked` — see `web/shapes/company-blocked.md`.
 *
 * The screen a signed-in user lands on when the gate ahead of them is closed. It is
 * not an error page: their credentials worked, their account is fine, and the thing
 * that is off is named along with the code and the person who can switch it on.
 *
 * Which of the two closures it is comes from `/auth/me` — `service_enabled: false`
 * (the instance kill switch) or the active membership's `enabled: false` (this one
 * company). `CompanyDisabled` keeps the two codes apart; this screen only decides
 * which to pass, and prefers `service` when both are true because the instance-wide
 * switch is the one that has to be cleared first.
 */

import type { ReactNode } from 'react';

import { activeCompany, useSession } from '../auth/SessionProvider';
import { useT } from '../i18n';
import { CompanyDisabled } from '../states/CompanyDisabled';
import { RetryAction } from '../states/RetryAction';
import { ScreenHeading } from '../shell/ScreenHeading';
import type { SessionUser } from '../api/types';

/**
 * `null` when the gate is open. Exported so the router asks exactly the question the
 * screen answers, rather than re-deriving it and drifting.
 */
export function blockedReason(user: SessionUser): 'company' | 'service' | null {
  if (!user.service_enabled) return 'service';
  const active = user.companies.find((c) => c.company_id === user.active_company_id) ?? null;
  if (active !== null && !active.enabled) return 'company';
  return null;
}

export function CompanyBlockedScreen(): ReactNode {
  const t = useT();
  const { user, refresh, signOut } = useSession();
  if (user === null) return null;

  const reason = blockedReason(user) ?? 'company';
  const company = activeCompany(user);

  return (
    <div className="wp-centred">
      {/* The desk edge: the shell's own 240px rail, with the brand at the top of it
          and no nav. Paper fills the rest of the page (finish review, fix 6). */}
      <div className="wp-door__edge">
        <p className="wp-door__brand">{t('app.name')}</p>
      </div>
      <div className="wp-centred__inner wp-centred__inner--wide wp-stack">
        <ScreenHeading>
          {reason === 'company'
            ? t('state.companyDisabled.company.title')
            : t('state.companyDisabled.service.title')}
        </ScreenHeading>

        <CompanyDisabled
          reason={reason}
          companyName={company?.name ?? null}
          actions={
            <>
              <RetryAction onRetry={refresh} testId="company-blocked-retry" />
              <button
                type="button"
                className="wp-button"
                onClick={() => void signOut()}
                data-testid="company-blocked-signout"
              >
                {t('common.signOut')}
              </button>
            </>
          }
        />

        <p className="wp-small wp-muted">{t('companyBlocked.retryHint')}</p>
      </div>
    </div>
  );
}
